import type { AxiosResponse } from 'axios'
import api from '../utils/api'
import { type ReportPermissionGroupPayload } from '../features/ReportPermissionsFeature/types'
import { type ResultsThreshold } from '../features/ResultsThresholdFeature/types'

// CE-124 — Report Permission Configuration.
// All routes live on the backend `report_permissions` blueprint. GETs are gated by
// SurveyProject.READ, the bulk-save PATCH by SurveyProject.EDIT. Saves are replace-all:
// the body is the full set of groups for the scope; `group_id` is server-generated.
//
// The role-listing endpoint (`/report_permissions/roles/?role_type=`) is consumed directly
// by MultiSelectApiAutoComplete in PermissionGroupCard (which takes an endpoint string), so
// it has no manager method here.
const BASE = '/api/report_permissions'

/** Series-level defaults (scope = distribution series). */
const getSeriesReportPermissions = async (seriesId: string): Promise<AxiosResponse> => {
  return await api.get(`${BASE}/distribution_series/${seriesId}/`)
}

const saveSeriesReportPermissions = async (
  seriesId: string,
  groups: ReportPermissionGroupPayload[]
): Promise<AxiosResponse> => {
  return await api.patch(`${BASE}/distribution_series/${seriesId}/`, groups)
}

/** Slot-level overrides (scope = slot / one survey-project instance). */
const getSlotReportPermissions = async (slotId: string): Promise<AxiosResponse> => {
  return await api.get(`${BASE}/slots/${slotId}/`)
}

const saveSlotReportPermissions = async (
  slotId: string,
  groups: ReportPermissionGroupPayload[]
): Promise<AxiosResponse> => {
  return await api.patch(`${BASE}/slots/${slotId}/`, groups)
}

// CE-210 — Results Threshold (minimum-response privacy floor). Sibling routes on the same
// `report_permissions` blueprint, scoped to a distribution series. GET is gated by
// SurveyProject.READ, PATCH by SurveyProject.EDIT. The PATCH is full-state: sending a `null`
// minimum (or an empty body) clears the floor.
const getSeriesResultsThreshold = async (seriesId: string): Promise<AxiosResponse> => {
  return await api.get(`${BASE}/distribution_series/${seriesId}/results_threshold/`)
}

const saveSeriesResultsThreshold = async (
  seriesId: string,
  threshold: ResultsThreshold
): Promise<AxiosResponse> => {
  return await api.patch(`${BASE}/distribution_series/${seriesId}/results_threshold/`, threshold)
}

const ReportPermissionManager = {
  getSeriesReportPermissions,
  saveSeriesReportPermissions,
  getSlotReportPermissions,
  saveSlotReportPermissions,
  getSeriesResultsThreshold,
  saveSeriesResultsThreshold
}

export default ReportPermissionManager
